/**
 * Indicator math — pure functions on OHLCV arrays.
 * Each takes `candles` (array of {time,open,high,low,close,volume}) and
 * returns rows in `{time, value}` format ready for a lineSeries.
 */

export function ema(candles, period) {
  if (!candles?.length || !period) return [];
  const k = 2 / (period + 1);
  const out = [];
  let sum = 0;
  let prev;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i].close;
    if (i < period - 1) {
      sum += c;
      continue;
    }
    if (i === period - 1) {
      sum += c;
      prev = sum / period;             // SMA seed on the (period-1)th bar
      out.push({ time: candles[i].time, value: +prev.toFixed(6) });
      continue;
    }
    prev = (c * k) + (prev * (1 - k)); // EMA thereafter — matches TradingView ta.ema
    out.push({ time: candles[i].time, value: +prev.toFixed(6) });
  }
  return out;
}

export function rsi(candles, period = 14) {
  if (!candles?.length) return [];
  const out = [];
  let gain = 0; let loss = 0;
  for (let i = 1; i <= period && i < candles.length; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    if (diff >= 0) gain += diff; else loss -= diff;
  }
  gain /= period; loss /= period;
  for (let i = period; i < candles.length; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    const g = diff >= 0 ? diff : 0;
    const l = diff < 0 ? -diff : 0;
    gain = ((gain * (period - 1)) + g) / period;
    loss = ((loss * (period - 1)) + l) / period;
    const rs = loss === 0 ? 100 : gain / loss;
    const v = loss === 0 ? 100 : 100 - (100 / (1 + rs));
    out.push({ time: candles[i].time, value: +v.toFixed(2) });
  }
  return out;
}

/**
 * VWAP — session-anchored (resets at each new day boundary in the
 * candle series). Uses typical price (h+l+c)/3 weighted by volume.
 */
export function vwap(candles) {
  if (!candles?.length) return [];
  const out = [];
  let cumPV = 0; let cumV = 0;
  let lastDay = null;
  for (const c of candles) {
    const day = Math.floor(c.time / 86400);
    if (lastDay != null && day !== lastDay) { cumPV = 0; cumV = 0; }
    lastDay = day;
    const tp = (c.high + c.low + c.close) / 3;
    cumPV += tp * (c.volume || 0);
    cumV += (c.volume || 0);
    if (cumV <= 0) continue;
    out.push({ time: c.time, value: +(cumPV / cumV).toFixed(6) });
  }
  return out;
}
